import { inject, signal, Service } from '@angular/core';
import { StorageService } from '../modules/checklist/services/storage.service';

export type TextSizeLevel = 'small' | 'default' | 'large' | 'larger';

const TEXT_SCALE_PROPERTY = '--app-text-scale';

const TEXT_SIZES: Record<TextSizeLevel, { scale: number; name: string }> = {
  small: { scale: 0.8, name: 'Small' },
  default: { scale: 1, name: 'Default' },
  large: { scale: 1.25, name: 'Large' },
  larger: { scale: 1.5, name: 'Larger' },
};

const RETIRED_LEVEL_REPLACEMENTS: Record<string, TextSizeLevel> = {
  largest: 'larger',
};

export const TEXT_SIZE_LEVELS = Object.keys(TEXT_SIZES) as TextSizeLevel[];

export function isTextSizeLevel(value: string | null): value is TextSizeLevel {
  return value !== null && Object.hasOwn(TEXT_SIZES, value);
}

@Service()
export class TextSizeService {
  private readonly storageService = inject(StorageService);
  private readonly rootElement = document.documentElement;

  private readonly levelSignal = signal<TextSizeLevel>(this.getSavedLevel());

  readonly currentLevel = this.levelSignal.asReadonly();

  setLevel(level: TextSizeLevel): void {
    const resolved = isTextSizeLevel(level) ? level : 'default';
    this.storageService.saveTextSize(resolved);
    this.applyLevel(resolved);
  }

  initializeTextSize(): void {
    this.applyLevel(this.getSavedLevel());
  }

  scaleFor(level: TextSizeLevel = this.currentLevel()): number {
    return TEXT_SIZES[level].scale;
  }

  labelFor(level: TextSizeLevel): string {
    return `${TEXT_SIZES[level].name} (${this.percentFor(level)})`;
  }

  percentFor(level: TextSizeLevel): string {
    return `${Math.round(TEXT_SIZES[level].scale * 100)}%`;
  }

  private applyLevel(level: TextSizeLevel): void {
    this.rootElement.style.setProperty(
      TEXT_SCALE_PROPERTY,
      String(TEXT_SIZES[level].scale)
    );
    this.levelSignal.set(level);
  }

  private getSavedLevel(): TextSizeLevel {
    const savedLevel = this.storageService.loadTextSize();
    if (isTextSizeLevel(savedLevel)) {
      return savedLevel;
    }

    return savedLevel !== null && savedLevel in RETIRED_LEVEL_REPLACEMENTS
      ? RETIRED_LEVEL_REPLACEMENTS[savedLevel]
      : 'default';
  }
}
